import { LANGUAGE_LABEL, LANGUAGE_SHORT_LABEL, LANGUAGES } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';

import { FlagIcon } from './FlagIcon';

/**
 * The EN/ES toggle [Figma · Language Selector, 12021:40379].
 *
 * **A single segmented pill**, not two separate buttons: a `--muted` track with a `--neutral-200` hairline,
 * 4px padding, and the selected language riding on a white `--card` pill inside it. The earlier build
 * rendered two large outlined pills reading "English" and "Español", which is a different control.
 *
 * Figma draws the whole thing as ONE button containing two divs. That is not built here: two mutually
 * exclusive choices need two focusable controls, or the toggle is unusable by keyboard and announces
 * nothing useful. The visual is the frame's; the semantics are corrected.
 *
 * The visible label is a flag and a code; the accessible name is the language's own name, because a screen
 * reader announcing "E N" tells the listener nothing. The flag is DRAWN, not typed — see `FlagIcon` for why
 * the emoji it replaces was invisible on Windows, which is half the hardware decision 0140 sanctions. Pressed state rides on `aria-pressed`, never on colour
 * alone.
 */
export function LanguageToggle() {
  const t = useTranslation();
  const language = useKioskSession((state) => state.language);
  const setLanguage = useKioskSession((state) => state.setLanguage);

  return (
    <div
      aria-label={t('welcome.languageLabel')}
      className="box-border flex items-center gap-1 rounded-[var(--radius-full)] border border-solid border-[var(--neutral-200)] bg-[var(--muted)] p-[3px]"
      role="group"
    >
      {LANGUAGES.map((code) => (
        <button
          aria-label={LANGUAGE_LABEL[code]}
          aria-pressed={code === language}
          className={`kiosk-focusable flex items-center gap-[4.5px] rounded-[var(--radius-full)] px-[10px] py-1 text-sm font-semibold leading-4 tracking-[0.21px] ${selectedStyle(code === language)}`}
          key={code}
          onClick={() => {
            setLanguage(code);
          }}
          type="button"
        >
          <FlagIcon code={code} />
          {LANGUAGE_SHORT_LABEL[code]}
        </button>
      ))}
    </div>
  );
}

/**
 * The selected segment is a white pill on the muted track; the other is bare.
 *
 * The 44px touch floor is deliberately NOT applied here. It is a chrome control at the top of the screen,
 * not part of the flow, and forcing it to 44px would inflate the pill to roughly twice its drawn height —
 * the frame's own hit area is ~28px and the two segments sit side by side, which is a designed trade the
 * convention's blanket rule would override. Flagged rather than silently taken.
 */
function selectedStyle(isSelected: boolean): string {
  return isSelected
    ? 'bg-[var(--card)] text-[var(--foreground)]'
    : 'text-[var(--muted-foreground)]';
}
